import { blue, grey } from '@ant-design/colors'
import { DownloadOutlined } from '@ant-design/icons'
import { Alert, Button, Col, Collapse, Row, Spin, Table, Typography } from 'antd'
import { FunctionComponent, useEffect, useState } from 'react'
import styled from 'styled-components'

import CustomButton from 'components/CustomButton'
import Spacer from 'components/Spacer'

import { api } from 'services/api'

import displayMessage from 'utils/displayMessage'

import * as T from 'types'

import { SubsectionTitle } from './Styles'

const { Panel } = Collapse
const { Text, Link } = Typography

const LoopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid ${grey[2]};
  &:last-child {
    border-bottom: none;
  }
`

interface ILoopDetailPanelProps {
  listingId: string
  loop: T.IDotloopLoop
}

const LoopDetailPanel: FunctionComponent<ILoopDetailPanelProps> = ({ listingId, loop }) => {
  const [detail, setDetail] = useState<T.IDotloopLoopDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Per-document, so one slow download does not spin every other row. */
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const handleExpand = async (keys: string | string[]) => {
    if (!keys.length || detail) return

    setLoading(true)
    setError(null)
    try {
      const response = await api.getDotloopLoopDetail(listingId, loop.loopId)

      if (!response.ok || !response.data) {
        throw new Error((response.data as unknown as string) || 'Failed to load loop details')
      }

      setDetail(response.data)
    } catch (err: any) {
      setError(err?.message || 'Failed to load loop details')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Save one document as a PDF.
   *
   * The bytes are proxied through our API rather than fetched from Dotloop —
   * they are only reachable with the app's OAuth token, which stays on the
   * server. So this arrives as an ArrayBuffer to be handed to the browser as
   * a blob, not as a link we could point at.
   */
  const handleDownload = async (folderId: string, doc: T.IDotloopDocument) => {
    setDownloadingId(doc.id)
    try {
      const response = await api.downloadDotloopDocument(listingId, loop.loopId, folderId, doc.id)

      if (!response.ok || !response.data) {
        throw new Error('Failed to download document')
      }

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)

      try {
        const a = window.document.createElement('a')
        a.href = url
        // Dotloop names documents without an extension; the browser saves from
        // a blob URL, so the name has to carry one or the file lands extensionless.
        a.download = doc.name.toLowerCase().endsWith('.pdf') ? doc.name : `${doc.name}.pdf`
        window.document.body.appendChild(a)
        a.click()
        a.remove()
      } finally {
        // Not revoking leaks the whole PDF until the tab closes.
        window.URL.revokeObjectURL(url)
      }
    } catch (err: any) {
      displayMessage('error', err?.message || 'Failed to download document')
    } finally {
      setDownloadingId(null)
    }
  }

  // Built per folder rather than once: the download call needs the folder id,
  // and a document id alone does not address a document in Dotloop.
  const documentColumnsFor = (folderId: string) => [
    { title: 'Document', dataIndex: 'name', key: 'name', width: 220 },
    {
      title: 'Signers',
      key: 'signatures',
      render: (_: unknown, doc: T.IDotloopDocument) => {
        if (!doc.signatures?.length) {
          return (
            <Text
              type="secondary"
              style={{ fontSize: 12 }}
            >
              —
            </Text>
          )
        }

        return (
          <div>
            {doc.signatures.map(sig => {
              const signed = sig.status === 'SIGNED'

              return (
                <div key={sig.email}>
                  <Text
                    type={signed ? 'success' : 'secondary'}
                    style={{ fontSize: 12 }}
                  >
                    {signed ? '✓' : '○'} {sig.name}
                    {sig.role ? ` (${sig.role})` : ''}
                    {' — '}
                    {signed && sig.signedAt ? new Date(sig.signedAt).toLocaleString() : sig.status}
                  </Text>
                </div>
              )
            })}
          </div>
        )
      },
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (val: string) => (val ? new Date(val).toLocaleString() : '—'),
    },
    {
      title: '',
      key: 'download',
      width: 50,
      render: (_: unknown, doc: T.IDotloopDocument) => (
        <Button
          type="text"
          size="small"
          icon={<DownloadOutlined />}
          loading={downloadingId === doc.id}
          onClick={() => handleDownload(folderId, doc)}
          // The row's text is the document name, so the icon button needs its
          // own label to say which document it saves.
          aria-label={`Download ${doc.name}`}
          title="Download PDF"
        />
      ),
    },
  ]

  return (
    <Collapse
      ghost
      onChange={handleExpand}
    >
      <Panel
        key="detail"
        header={<span style={{ fontWeight: 500 }}>{loop.loopName}</span>}
        extra={(
          <Text
            type="secondary"
            style={{ fontSize: 12 }}
          >
            {loop.createdAt ? new Date(loop.createdAt).toLocaleDateString() : ''}
          </Text>
        )}
      >
        {loading && <Spin size="small" />}
        {error && (
          <Alert
            type="error"
            message={error}
            style={{ marginBottom: 8 }}
          />
        )}
        {detail && (
          <>
            <Row
              gutter={[8, 4]}
              style={{ marginBottom: 8 }}
            >
              <Col>
                <Text type="secondary">Status: </Text>
                <Text>{detail.status || '—'}</Text>
              </Col>
              <Col>
                <Text type="secondary">Template: </Text>
                <Text>{detail.templateName || '—'}</Text>
              </Col>
              <Col>
                <Text type="secondary">Created: </Text>
                <Text>{detail.createdAt ? new Date(detail.createdAt).toLocaleString() : '—'}</Text>
              </Col>
            </Row>
            {detail.folders?.map(folder => (
              <div
                key={folder.folderId}
                style={{ marginBottom: 12 }}
              >
                <Text
                  strong
                  style={{ display: 'block', marginBottom: 4 }}
                >
                  {folder.folderName}
                </Text>
                <Table
                  dataSource={folder.documents}
                  columns={documentColumnsFor(folder.folderId)}
                  rowKey="id"
                  size="small"
                  pagination={false}
                />
              </div>
            ))}
            {detail.note && (
              <Alert
                type="info"
                message={detail.note}
                style={{ marginTop: 8 }}
              />
            )}
            <div style={{ marginTop: 12 }}>
              <Link
                href={detail.dotloopUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Dotloop →
              </Link>
            </div>
          </>
        )}
      </Panel>
    </Collapse>
  )
}

interface IProps {
  listing: T.IListing
}

const DotloopSection: FunctionComponent<IProps> = ({ listing }) => {
  const [creating, setCreating] = useState(false)
  const [loadingLoops, setLoadingLoops] = useState(false)
  const [loops, setLoops] = useState<T.IDotloopLoop[] | null>(null)
  const [loopsError, setLoopsError] = useState<string | null>(null)
  const [createAlert, setCreateAlert] = useState<T.IAlert | null>(null)

  const fetchLoops = async () => {
    setLoadingLoops(true)
    setLoopsError(null)
    try {
      const response = await api.getDotloopLoops(listing._id)

      if (!response.ok || !response.data) {
        throw new Error('Failed to load loops')
      }

      setLoops(response.data.loops)
    } catch (err: any) {
      setLoopsError(err?.message || 'Failed to load loops')
    } finally {
      setLoadingLoops(false)
    }
  }

  useEffect(() => {
    fetchLoops()
  }, [listing._id])

  const handleCreateLoop = async () => {
    setCreating(true)
    setCreateAlert(null)
    try {
      const response = await api.createDotloopLoop(listing._id)

      if (!response.ok || !response.data) {
        const msg = (response.data as any)?.message || 'Failed to create loop'
        throw new Error(msg)
      }

      const { loopName, dotloopUrl } = response.data
      setCreateAlert({
        type: 'success',
        message: `Loop created: ${loopName}`,
      })
      // Refresh the loop list after creation
      fetchLoops()
      displayMessage('success', `Dotloop loop created: ${loopName}`)
      if (typeof window !== 'undefined') {
        window.open(dotloopUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (err: any) {
      const msg = err?.message || 'An error occurred while creating the loop'
      setCreateAlert({ type: 'error', message: msg })
      displayMessage('error', msg)
    } finally {
      setCreating(false)
    }
  }

  return (
    <>
      <Spacer size={20} />
      <SubsectionTitle>Dotloop</SubsectionTitle>
      <Row
        align="middle"
        gutter={[15, 15]}
      >
        <Col>
          <CustomButton
            backgroundColor={blue[5]}
            backgroundColorOnHover={blue[6]}
            custom
            loading={creating}
            onClick={handleCreateLoop}
          >
            Create Dotloop Loop
          </CustomButton>
        </Col>
        <Col>
          <CustomButton
            backgroundColor={loops !== null ? grey[5] : grey[3]}
            backgroundColorOnHover={grey[5]}
            custom
            loading={loadingLoops}
            onClick={fetchLoops}
            disabled={loadingLoops}
          >
            {loops !== null ? `Loops (${loops.length})` : 'Load Loops'}
          </CustomButton>
        </Col>
        {createAlert && (
          <Col>
            <Alert
              style={{ height: 32 }}
              message={createAlert.message}
              type={createAlert.type}
              showIcon
              closable
              onClose={() => setCreateAlert(null)}
            />
          </Col>
        )}
      </Row>
      {loopsError && (
        <>
          <Spacer size={8} />
          <Alert
            type="error"
            message={loopsError}
            closable
            onClose={() => setLoopsError(null)}
          />
        </>
      )}
      {loops !== null && (
        <>
          <Spacer size={12} />
          {loops.length === 0
            ? (
              <Text type="secondary">No loops found for this listing.</Text>
            )
            : (
              loops.map(loop => (
                <LoopRow key={loop.loopId}>
                  <div style={{ flex: 1 }}>
                    <LoopDetailPanel
                      listingId={listing._id}
                      loop={loop}
                    />
                  </div>
                </LoopRow>
              ))
            )}
        </>
      )}
    </>
  )
}

export default DotloopSection
